import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MemoryAnswersV1 } from '../MemoryAnswersV1';
import { MEMORY_MAP_SOUNDS } from '../../shared/memory-map-assets';
import { useAutoSubmitAnswer } from '../../shared/use-auto-submit-answer';
import styles from './QuestionSoundSlideV1.css';

const BARS = Array.from({ length: 32 }, (_, position) => ({ position, height: 6 + Math.round(Math.abs(Math.sin(position * 1.7 + 0.4)) * 18) }));
const FALLBACK_HINT = 'This sound can’t play here. Continue without it.';

function formatTime(seconds) {
    return `0:${String(Math.max(0, Math.ceil(seconds))).padStart(2, '0')}`;
}

function QuestionSoundSlideV1({ id, question, hint, soundId, skipLabel, answers, submitAnswer, submitTemporaryAnswer, userResponse = {}, delay }) {
    const { selectAnswer } = useAutoSubmitAnswer({ id, question, userResponse, submitAnswer, submitTemporaryAnswer, delay });
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [hasError, setHasError] = useState(false);
    const playedBars = Math.round(progress * BARS.length);

    useEffect(() => () => {
        audioRef.current?.pause();
        audioRef.current = null;
    }, []);

    const createAudio = () => {
        const audio = new Audio(MEMORY_MAP_SOUNDS[soundId]);

        audio.ontimeupdate = () => {
            if (audio.duration) {
                setProgress(audio.currentTime / audio.duration);
                setTimeLeft(audio.duration - audio.currentTime);
            }
        };
        audio.onended = () => {
            setIsPlaying(false);
            setProgress(1);
        };

        return audio;
    };

    const handleToggle = () => {
        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlaying(false);

            return;
        }

        audioRef.current = audioRef.current || createAudio();
        audioRef.current.play()
            .then(() => setIsPlaying(true))
            .catch(() => setHasError(true));
    };

    const handleSkip = () => {
        submitAnswer({ questionId: id, question, text: skipLabel, isSkipped: true })();
    };

    return (
        <div className={styles.form} key={id}>
            <div className={styles.player}>
                <button
                    type='button'
                    className={styles.play}
                    onClick={handleToggle}
                    aria-label={isPlaying ? 'Pause the sound' : 'Play the sound'}
                >
                    {isPlaying ? (
                        <svg
                            width='20' height='20' viewBox='0 0 20 20'
                            aria-hidden='true'
                        >
                            <rect
                                x='5' y='4.5' width='3.5'
                                height='11' rx='1' fill='currentColor'
                            />
                            <rect
                                x='11.5' y='4.5' width='3.5'
                                height='11' rx='1' fill='currentColor'
                            />
                        </svg>
                    ) : (
                        <svg
                            width='20' height='20' viewBox='0 0 20 20'
                            aria-hidden='true'
                        >
                            <path d='M6.5 4.2v11.6a.8.8 0 0 0 1.2.7l9-5.8a.8.8 0 0 0 0-1.4l-9-5.8a.8.8 0 0 0-1.2.7z' fill='currentColor' />
                        </svg>
                    )}
                </button>
                <div className={styles.wave} aria-hidden='true'>
                    {BARS.map(({ position, height }) => (
                        <i
                            key={position}
                            className={position < playedBars ? styles.barPlayed : styles.bar}
                            style={{ height: `${height}px` }}
                        />
                    ))}
                </div>
                <span className={styles.time}>{timeLeft === null ? '0:12' : formatTime(timeLeft)}</span>
            </div>
            <div className={styles.rest}>
                <div className={styles.text}>
                    <div className={styles.title}>
                        <h1 className={styles.question}>{question}</h1>
                        <p className={styles.hint} role='status'>{hasError ? FALLBACK_HINT : hint}</p>
                    </div>
                    <MemoryAnswersV1
                        answers={answers}
                        selectedId={userResponse?.id}
                        onSelect={selectAnswer}
                    />
                </div>
                <button type='button' className={styles.skip} onClick={handleSkip}>{skipLabel}</button>
            </div>
        </div>
    );
}

QuestionSoundSlideV1.propTypes = {
    id: PropTypes.number.isRequired,
    question: PropTypes.string.isRequired,
    hint: PropTypes.string,
    soundId: PropTypes.string.isRequired,
    skipLabel: PropTypes.string.isRequired,
    answers: PropTypes.array.isRequired,
    submitAnswer: PropTypes.func.isRequired,
    submitTemporaryAnswer: PropTypes.func.isRequired,
    userResponse: PropTypes.object,
    delay: PropTypes.number
};

export {
    QuestionSoundSlideV1
};
